import { TimeoutError } from '../errors'

/**
 * Run a promise with a timeout. If the promise finishes first (resolves or rejects), withTimeout will return the
 * result of the promise, but if the promise is still running after the timeout, withTimeout will reject with a
 * TimeoutError.
 *
 * @param promise The promise to run, with a timeout
 * @param timeoutMs The timeout, in milliseconds
 * @returns The result of running the promise, or a TimeoutError
 */
export const withTimeout = async <T>(promise: Promise<T> | (() => Promise<T>), timeoutMs: number): Promise<T> => {
  let timeout: NodeJS.Timeout | undefined = undefined
  return Promise.race([
    typeof promise === 'function' ? promise() : promise,
    new Promise<T>((_resolve, reject) => {
      timeout = setTimeout(() => {
        reject(new TimeoutError(`Timeout of ${timeoutMs}ms exceeded`))
      }, timeoutMs)
    }),
  ]).then(
    // For efficiency, cleanup the timeout on success
    (successfulResult) => {
      timeout && clearTimeout(timeout)
      return successfulResult
    },
    // ... and also clean it up on error
    (error) => {
      timeout && clearTimeout(timeout)
      throw error
    },
  )
}
